/**
 * @property POSITIVE
 * @property NEGATIVE
 * @readonly
 * @enum {string} Sign
 */
export const SIGN = {
	POSITIVE: 'POSITIVE',
	NEGATIVE: 'NEGATIVE',
};

export const SECOND_TO_SECOND = 1;
export const MINUTE_TO_SECOND = 60 * SECOND_TO_SECOND;
export const HOUR_TO_SECOND = 60 * MINUTE_TO_SECOND;
export const DAY_TO_SECOND = 24 * HOUR_TO_SECOND;

/**
 * @property YEAR
 * @property MONTH
 * @property WEEK
 * @property DAY
 * @property HOUR
 * @property MINUTE
 * @property SECOND
 * @property MILLISECOND
 * @property MICROSECOND
 * @readonly
 * @enum {string} Unit
 */
export const UNIT = {
	YEAR: 'year',
	MONTH: 'month',
	WEEK: 'week',
	DAY: 'day',
	HOUR: 'hour',
	MINUTE: 'minute',
	SECOND: 'second',
	MILLISECOND: 'millisecond',
	MICROSECOND: 'microsecon',
};

const timeRelation = {
	future: 'dans %s',
	past: 'il y a %s',
	[UNIT.SECOND]: 'quelques secondes',
	// [UNIT.MINUTE]: 'a minute',
	[UNIT.MINUTE]: '%s minutes',
	// [UNIT.HOUR]: 'an hour',
	[UNIT.HOUR]: '%s heures',
	// [UNIT.DAY]: 'a day',
	[UNIT.DAY]: '%s jours',
	// [UNIT.MONTH]: 'a month',
	[UNIT.MONTH]: '%s mois',
	// [UNIT.YEAR]: 'a year',
	[UNIT.YEAR]: '%s ans',
};

/** @type {[number, string][]} */
const timetable = [
	[0, UNIT.SECOND],
	[45, UNIT.SECOND],
	[90, UNIT.MINUTE],
	[2640, UNIT.MINUTE],
	[5341, UNIT.HOUR],
	[75_601, UNIT.HOUR],
	[126_001, UNIT.DAY],
	[25 * 86_400, UNIT.DAY],
	[45 * 86_400, UNIT.MONTH],
	[319 * 86_400, UNIT.MONTH],
	[547 * 86_400, UNIT.YEAR],
];

export const REGEX_FORMAT = /\[([^\]]+)]|Y{2,4}|M{1,4}|D{1,2}|d{1,4}|H{1,2}|h{1,2}|a|A|m{1,2}|s{1,2}|Z{1,2}|SSS|u{5}/g;

/**
 * @typedef {Object} ParsedDate
 * @property {number} microseconds
 * @property {number} seconds
 * @property {number} days
 * @property {SIGN} sign
 */

const AMOUNT_OF_DAYS_BY_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const AMOUNT_OF_DAYS_BY_MONTH_LEAP = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const AMOUNT_OF_DAYS_BY_MONTH_CUMULATIVE = [31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334, 365];
const AMOUNT_OF_DAYS_BY_MONTH_CUMULATIVE_LEAP = [31, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335, 366];

const AMOUNT_OF_DAYS_BY_YEAR_CUMUL = {
	100: 36_160,
	500: 182_257,
	1000: 364_878,
	1500: 547_499,
	1550: 565_761,
	1600: 584_023,
	1650: 602_286,
	1700: 620_548,
	1750: 638_810,
	1800: 657_072,
	1850: 675_334,
	1900: 693_596,
	1950: 711_858,
	2000: 730_120, // le 2001-01-01 => 730120 jours cumulés depuis l'an 1 (à la date du 0001/01/01)
	2005: 731_947,
	2010: 733_773,
	2011: 734_138,
	2012: 734_503,
	2013: 734_869,
	2014: 735_234,
	2015: 735_599,
	2016: 735_964,
	2017: 736_330,
	2018: 736_695,
	2019: 737_060,
	2020: 737_425,
	2021: 737_791,
	2022: 738_156,
	2023: 738_521,
	2024: 738_886,
	2025: 739_252,
	2026: 739_617,
	2027: 739_982,
	2028: 740_347,
	2029: 740_713,
	2030: 741_078,
	2040: 744_730,
	2050: 748_383,
	2100: 766_645,
	3000: 1_095_363,
	4000: 1_460_605,
	5000: 1_825_848,
	10_000: 3_652_060,
	20_000: 7_304_485,
	30_000: 10_956_910,
	40_000: 14_609_335,
	50_000: 18_261_760,
	100_000: 36_523_885,
	150_000: 54_786_010,
	200_000: 73_048_135,
	250_000: 91_310_260,
	300_000: 109_572_385,
	400_000: 146_096_635,
	500_000: 182_620_885,
	600_000: 219_145_135,
	700_000: 255_669_385,
	800_000: 292_193_635,
	900_000: 328_717_885,
	1_000_000: 365_242_135,
};

export default class KDate {
	#years = 0

	#months = 0

	#days = 0

	#hours = 0

	#minutes = 0

	#seconds = 0

	#microseconds = 0

	#sign = SIGN.POSITIVE

	static now() {
		return KDate.fromDate(new Date());
	}

	static fromNumber(number) {
		return KDate.fromString(number.toString());
	}

	/**
	 * Resolve date to local timezone
	 *
	 * @param {number} [offset]
	 */
	local(offset = new Date().getTimezoneOffset()) {
		const clone = new KDate()
			.setYears(this.#years)
			.setMonths(this.#months)
			.setDays(this.#days)
			.setHours(this.#hours)
			.setMinutes(this.#minutes)
			.setSeconds(this.#seconds)
			.setMicroSeconds(this.#microseconds);
		clone.addMinutes(-offset);
		return clone;
	}

	/**
	 * Parse date and return every part
	 *
	 * @param {string} date The date to parse
	 * @returns {Object}
	 */
	static parse(date) {
		const regex = /^\d*$/;

		if (!regex.test(date)) {
			throw new Error('Invalid Format');
		}

		let sign = SIGN.POSITIVE;

		let dateArray = date.split('');
		if (dateArray[dateArray.length - 1] === '-') {
			sign = SIGN.NEGATIVE;
			dateArray.pop();
		}

		dateArray = dateArray.join('').padStart(11, '0').split('');

		const microseconds = Number.parseInt(dateArray.splice(-5, 5).join(''), 10);
		const seconds = Number.parseInt(dateArray.splice(-5, 5).join(''), 10);
		const days = Number.parseInt(dateArray.join(''), 10);

		return {
			microseconds, seconds, days, sign,
		};
	}

	/**
	 * @param {string} kdate A date in the KDate format
	 * (<nb_jours depuis l'an 0><nb_seconde de la journée(5)><nb_micro de la seconde(5)>)
	 * @returns {KDate} The new Kdate
	 */
	static fromString(kdate) {
		const parsed = KDate.parse(kdate);

		let parsedSeconds = parsed.seconds;
		let parsedDays = parsed.days;

		// Time
		const hours = Math.floor(parsedSeconds / 3600);
		parsedSeconds -= hours * 3600;
		const minutes = Math.floor(parsedSeconds / 60);
		parsedSeconds -= minutes * 60;
		const seconds = parsedSeconds;
		const { microseconds } = parsed;

		// Date
		let years = 0;

		let cumul = 0;
		for (const [key, temporaryCumul] of Object.entries(AMOUNT_OF_DAYS_BY_YEAR_CUMUL)) {
			const temporaryYear = Number.parseInt(key, 10);
			if (temporaryCumul > parsedDays) {
				// eslint-disable-next-line no-continue
				continue;
			}
			years = temporaryYear;
			cumul = temporaryCumul - 1;
		}

		let	suite = true;
		let daysByYear;
		while (suite) {
			daysByYear = KDate.isLeap(years) ? 366 : 365;
			if (cumul + daysByYear >= parsedDays) {
				suite = false;
			}
			else {
				years++;
				cumul += daysByYear;
			}
		}

		parsedDays -= cumul;

		const bissextile = KDate.isLeap(years);
		suite = true;
		cumul = 0;
		let dateMonth = 0;
		while (suite) {
			const daysByMonth = bissextile ?
				AMOUNT_OF_DAYS_BY_MONTH_LEAP[dateMonth] :
				AMOUNT_OF_DAYS_BY_MONTH[dateMonth];
			if (cumul + daysByMonth >= parsedDays) {
				suite = false;
			}
			else {
				dateMonth++;
				cumul += daysByMonth;
			}
		}
		const months = dateMonth;
		parsedDays -= cumul;
		const days = parsedDays;

		const date = new KDate();
		date
			.setYears(years)
			.setMonths(months)
			.setDays(days)
			.setHours(hours)
			.setMinutes(minutes)
			.setSeconds(seconds)
			.setMicroSeconds(microseconds)
			// eslint-disable-next-line unicorn/consistent-destructuring
			.setSign(parsed.sign);

		return date;
	}

	/**
	 * Create a KDate from Now
	 *
	 * @returns {KDate} The new Kdate
	 */
	/* static fromNow() {
		return KDate.fromDate(new Date());
	} */

	/**
	 * Create a KDate from a regular date
	 *
	 * @param {Date} date
	 * @returns {KDate} The new Kdate
	 */
	static fromDate(date) {
		const kdate = new KDate();

		return kdate
			.setYears(date.getUTCFullYear())
			.setMonths(date.getUTCMonth())
			.setDays(date.getUTCDate())
			.setHours(date.getUTCHours())
			.setMinutes(date.getUTCMinutes())
			.setSeconds(date.getUTCSeconds())
			.setMicroSeconds(date.getUTCMilliseconds() * 100);
	}

	/**
	 * Check if a year is $bissextile
	 *
	 * @param {number} year The year to check
	 * @returns {boolean} - Wether the year is bissextile or not
	 */
	static isLeap(year) {
		return (
			(
				(year % 4 === 0) && // Divisible by 4
				(year % 100 !== 0) // Not divisible by 100
			) ||
			(year % 400) === 0 // Divisible by 400
		);
	}

	addYears(years) {
		this.#years += years;
		return this;
	}

	addMonths(months = 1) {
		const total = this.#months + months;

		const years = Math.floor(total / 12);
		if (years > 0) {
			this.addYears(years);
		}
		// eslint-disable-next-line no-param-reassign
		const remainingMonths = total % 12;

		this.#months = remainingMonths;
		return this;
	}

	addDays(days = 1) {
		const total = this.#days + days;

		/**
		 * TODO this is wrong
		 * Should be recalculated every month because number of days change
		 * Leap can change too
		 */

		const isLeap = KDate.isLeap(this.#years);
		const dividend = isLeap ?
			AMOUNT_OF_DAYS_BY_MONTH[this.#months] :
			AMOUNT_OF_DAYS_BY_MONTH_LEAP[this.#months];

		const months = Math.floor(total / dividend);
		if (months > 0) {
			this.addMonths(months);
		}
		// eslint-disable-next-line no-param-reassign
		const remainingdays = total % dividend;

		this.#days = remainingdays;
		return this;
	}

	addHours(hours = 1) {
		const total = this.#hours + hours;

		const days = Math.floor(total / 24);
		if (days > 0) {
			this.addDays(days);
		}
		// eslint-disable-next-line no-param-reassign
		const remainingHours = total % 24;

		this.#hours = remainingHours;
		return this;
	}

	addMinutes(minutes = 1) {
		const total = this.#minutes + minutes;

		const hours = Math.floor(total / 60);
		if (hours > 0) {
			this.addHours(hours);
		}
		// eslint-disable-next-line no-param-reassign
		const remainingminutes = total % 60;

		this.#minutes = remainingminutes;
		return this;
	}

	addSeconds(seconds = 1) {
		const total = this.#seconds + seconds;

		const minutes = Math.floor(total / 60);
		if (minutes > 0) {
			this.addMinutes(minutes);
		}
		// eslint-disable-next-line no-param-reassign
		const remainingseconds = total % 60;

		this.#seconds = remainingseconds;
		return this;
	}

	addMicroSeconds(mSeconds = 1) {
		const total = this.#microseconds + mSeconds;

		const seconds = Math.floor(total / 1_000_000);
		if (seconds > 0) {
			this.addSeconds(seconds);
		}
		// eslint-disable-next-line no-param-reassign
		const remainingmSeconds = total % 1_000_000;

		this.#microseconds = remainingmSeconds;
		return this;
	}

	setYears(years) {
		this.#years = years;
		return this;
	}

	setMonths(months) {
		this.#months = months;
		return this;
	}

	setDays(days = 1) {
		this.#days = days;
		return this;
	}

	setHours(hours = 1) {
		this.#hours = hours;
		return this;
	}

	setMinutes(minutes = 1) {
		this.#minutes = minutes;
		return this;
	}

	setSeconds(seconds = 1) {
		this.#seconds = seconds;
		return this;
	}

	setMicroSeconds(mSeconds = 1) {
		this.#microseconds = mSeconds;
		return this;
	}

	/**
	 *
	 * @param {KDate} kdate
	 * @param {UNIT} unit
	 * @returns {number}
	 */
	diff(kdate, unit) {
		/**
		 * @param {KDate} kdate1
		 * @param {KDate} kdate2
		 * @returns {number}
		 */
		// eslint-disable-next-line unicorn/consistent-function-scoping
		const days = (kdate1, kdate2) => {
			const daysDate1 = kdate1.asDays();
			const daysDate2 = kdate2.asDays();

			return daysDate2 - daysDate1;
		};

		/**
		 *
		 * @param {KDate} kdate1
		 * @param {KDate} kdate2
		 */
		// eslint-disable-next-line unicorn/consistent-function-scoping
		const seconds = (kdate1, kdate2) => {
			const daysDate1 = kdate1.asDays();
			const daysDate2 = kdate2.asDays();

			const sec1 = (daysDate1 * 24 * 60 * 60) + (kdate1.hour() * 60 * 60) + (kdate1.minute() * 60) + kdate1.second();
			const sec2 = (daysDate2 * 24 * 60 * 60) + (kdate2.hour() * 60 * 60) + (kdate2.minute() * 60) + kdate2.second();

			return sec2 - sec1;
		};

		// TODO https://stackoverflow.com/questions/4127363/date-difference-in-years-using-c-sharp
		// eslint-disable-next-line unicorn/consistent-function-scoping
		const years = (kdate1, kdate2) => {
			const yearDate1 = kdate1.year();
			const yearDate2 = kdate2.year();

			return yearDate2 - yearDate1;
		};

		// eslint-disable-next-line unicorn/consistent-function-scoping
		const hours = (kdate1, kdate2) => {
			const hourDate1 = kdate1.hour();
			const hourDate2 = kdate2.hour();

			return hourDate2 - hourDate1;
		};

		// eslint-disable-next-line unicorn/consistent-function-scoping
		const months = (kdate1, kdate2) => (
			(
				(kdate2.year() - kdate1.year()) * 12) +
				(kdate1.month() - kdate1.month())
		);

		const table = {
			[UNIT.SECOND]: seconds,
			[UNIT.HOUR]: hours,
			[UNIT.DAY]: days,
			[UNIT.MONTH]: months,
			[UNIT.YEAR]: years,
			default: () => {
				throw new Error(`Invalid Unit: ${unit}`);
			},
		};

		// See table here: https://momentjs.com/docs/#/displaying/fromnow/
		const result = unit in table ? table[unit]?.(this, kdate) : table.default();

		return result === 0 ? 1 : result; // Bwaaah
	}

	/* asDays() {
		let year = this.year();
		let month = this.month();
		const day = this.dayOfMonth();

		month = (month + 9) % 12;
		year -= Math.floor(month / 10);
		return 365 * year +
			Math.floor(year / 4) -
			Math.floor(year / 100) +
			Math.floor(year / 400) +
			Math.floor((month * 306 + 5) / 10) +
			(day - 1);
	} */

	asDays() {
		const year = this.#years;
		const month = this.#months;
		const day = this.#days;
		const sign = this.#sign;

		const isLeap = KDate.isLeap(year);

		let yearIndice = 1;
		let days = 0;
		Object.entries(AMOUNT_OF_DAYS_BY_YEAR_CUMUL).some(([key, cumul]) => {
			const y = Number.parseInt(key, 10);
			if (y > year) {
				return true;
			}
			yearIndice = y;
			days = cumul - 1;
			return false;
		});

		// Calculate remaining years between our table
		for (let index = yearIndice; index < year; index++) {
			days += KDate.isLeap(index) ? 366 : 365;
		}
		if (month - 1 >= 0) { // at least february
			days += (
				isLeap ?
					AMOUNT_OF_DAYS_BY_MONTH_CUMULATIVE_LEAP[month - 1] :
					AMOUNT_OF_DAYS_BY_MONTH_CUMULATIVE[month - 1]
			);
		}
		days += day;
		return (sign === SIGN.NEGATIVE) ? days * -1 : days;
	}

	/**
	 *
	 * @param {KDate} kdate
	 */
	from(kdate) {
		const seconds = kdate.diff(this, UNIT.SECOND);
		let unit = UNIT.YEAR;
		const timer = timetable.find(x => x[0] > seconds);
		if (timer) {
			unit = timer[1];
		}
		const sentence = timeRelation[seconds >= 0 ? 'past' : 'future'].replace('%s', timeRelation[unit]);
		const amountInUnit = kdate.diff(this, unit);

		return sentence.replace('%s', amountInUnit.toString());
	}

	/**
	 *
	 * @param {UNIT} unit
	 */
	// eslint-disable-next-line class-methods-use-this
	// eslint-disable-next-line no-unused-vars
	startOf(unit) {
		throw new Error('Not implemented');
	}

	/**
	 *
	 * @param {UNIT} unit
	 */
	// eslint-disable-next-line class-methods-use-this
	// eslint-disable-next-line no-unused-vars
	endOf(unit) {
		throw new Error('Not implemented');
	}

	/**
	 *
	 * @param {KDate} kdateBefore
	 * @param {KDate} kdateAfter
	 */
	// eslint-disable-next-line class-methods-use-this
	// eslint-disable-next-line no-unused-vars
	isBetween(kdateBefore, kdateAfter) {
		throw new Error('Not implemented');
	}

	/**
	 * Set the sign of the date: positive or negative
	 *
	 * @param {SIGN} sign - The sign of the date
	 */
	setSign(sign) {
		this.sign = sign;
		return this;
	}

	/* asDate() {
		// const fn = this.sign === SIGN.NEGATIVE ? 'substract' : 'add';
		const christDate = dayjs(new Date('0001-01-01'));
		const dateWithDays = christDate.add(this.#days, 'day');
		const dateWithSeconds = dateWithDays.add(this.#seconds, 'second');
		const finalDate = dateWithSeconds.add(this.#microseconds, 'millisecond');
		return finalDate.toDate();
	} */

	year() {
		return this.#years;
	}

	month() {
		return this.#months;
	}

	day() {
		return this.#days;
	}

	hour() {
		return this.#hours;
	}

	minute() {
		return this.#minutes;
	}

	second() {
		return this.#seconds;
	}

	microsecond() {
		return this.#microseconds;
	}

	format(format) {
		/**
		 * @param {string | number} input
		 * @param {number} padAmount
		 */
		// eslint-disable-next-line unicorn/consistent-function-scoping
		const prefix = (input, padAmount) => {
			if (typeof input === 'number') {
				// eslint-disable-next-line no-param-reassign
				input = input.toString();
			}
			return input.padStart(padAmount, '0');
		};

		const formats = {
			YY: prefix(this.year().toString().slice(-2), 2),
			YYYY: prefix(this.year(), 4),
			M: (this.month() + 1).toString(),
			MM: prefix(this.month() + 1, 2),
			DD: prefix(this.day(), 2),
			HH: prefix(this.hour(), 2),
			mm: prefix(this.minute(), 2),
			ss: prefix(this.second(), 2),
			uuuuu: prefix(this.microsecond(), 5),
			// MMM: getShort(locale.monthsShort, $M, months, 3),
			// MMMM: getShort(months, $M),
			// D: this.$D,
			// d: String(this.$W),
			// dd: getShort(locale.weekdaysMin, this.$W, weekdays, 2),
			// ddd: getShort(locale.weekdaysShort, this.$W, weekdays, 3),
			// dddd: weekdays[this.$W],
			// H: String($H),
			// h: get$H(1),
			// hh: get$H(2),
			// a: meridiemFunc($H, $m, true),
			// A: meridiemFunc($H, $m, false),
			// m: String($m),
			// s: String(this.$s),
			// SSS: Utils.s(this.$ms, 3, '0'),
			// Z: zoneStr // 'ZZ' logic below
		};
		return format.replace(REGEX_FORMAT, (match, $1) => $1 || formats[match]);

		// return `${formats.YYYY}-${formats.MM}-${formats.DD} ${formats.HH}:${formats.mm}:${formats.ss}`;
	}

	getSecondsFromDays() {
		const hours = this.#hours;
		const minutes = this.#minutes;
		const seconds = this.#seconds;

		const total = (hours * HOUR_TO_SECOND) + (minutes * MINUTE_TO_SECOND) + seconds;
		return total;
	}

	asString() {
		const days = this.asDays();
		// const seconds = this.#seconds.toString();
		const seconds = this.getSecondsFromDays().toString();
		const ms = '0';

		return `${days}${seconds.padStart(5, '0')}${ms.padStart(5, '0')}`;
	}

	asJSON() {
		return {
			years: this.#years,
			months: this.#months,
			days: this.#days,
			hours: this.#hours,
			minutes: this.#minutes,
			seconds: this.#seconds,
			microseconds: this.#microseconds,
			sign: this.#sign,
		};
	}
}
